import type { FilterValue } from "@/ds";
import {
  EMPTY_AUDIT_FILTERS,
  resolveAuditJustificativa,
  getAuditModuleLabel,
} from "@/modules/audit";
import type { AuditAction, AuditFilters, AuditModule, AuditRecord } from "@/modules/audit";
import { formatAuditDateTime } from "@/shared/components/audit-date-time/audit-date-time";
import { isEmail } from "@/shared/components/user-avatar";
import {
  AUDIT_ACTION_KEY,
  AUDIT_MODULE_KEY,
  AUDIT_RESPONSAVEL_KEY,
  AUDIT_SYSTEM_USER,
  AUDIT_YEAR_KEY,
} from "./audit.constants";
import type { AuditDetalheLinha, AuditLinha } from "./types-audit";

const inicioDoAno = (ano: string) => `${ano}-01-01`;
const fimDoAno = (ano: string) => `${ano}-12-31`;

/** Ano do período quando ele cobre um ano civil inteiro; "" sem período (ou com outro intervalo). */
function anoDoPeriodo({ dataInicio, dataFim }: AuditFilters): string {
  const ano = dataInicio.slice(0, 4);
  return dataInicio === inicioDoAno(ano) && dataFim === fimDoAno(ano) ? ano : "";
}

/** Critérios padrão da aba: o ano corrente inteiro (Figma 4407:581557, "Ano: 2026"), sem os demais filtros. */
export function filtrosDoAno(ano: number): AuditFilters {
  return { ...EMPTY_AUDIT_FILTERS, dataInicio: inicioDoAno(`${ano}`), dataFim: fimDoAno(`${ano}`) };
}

/** Anos do filtro Ano, do corrente para trás. */
export function opcoesDeAno(anoCorrente: number, anosAtras: number) {
  return Array.from({ length: anosAtras + 1 }, (_, indice) => {
    const ano = `${anoCorrente - indice}`;
    return { value: ano, label: ano };
  });
}

/** Filtros aplicados no formato da linha de filtros: cada campo é uma lista; o período vira o ano. */
export function toPanelValue(filters: AuditFilters): FilterValue {
  const ano = anoDoPeriodo(filters);

  return {
    [AUDIT_YEAR_KEY]: ano ? [ano] : [],
    [AUDIT_ACTION_KEY]: filters.alteracao,
    [AUDIT_MODULE_KEY]: filters.modulo ? [filters.modulo] : [],
    [AUDIT_RESPONSAVEL_KEY]: filters.responsavelId,
  };
}

/** O ano escolhido vira o período de 1º/jan a 31/dez; Alteração e Responsável seguem com todos os valores. */
export function fromPanelValue(value: FilterValue): AuditFilters {
  const ano = value[AUDIT_YEAR_KEY]?.[0] ?? "";

  return {
    ...EMPTY_AUDIT_FILTERS,
    dataInicio: ano ? inicioDoAno(ano) : "",
    dataFim: ano ? fimDoAno(ano) : "",
    alteracao: (value[AUDIT_ACTION_KEY] ?? []) as AuditAction[],
    modulo: (value[AUDIT_MODULE_KEY]?.[0] ?? "") as AuditModule | "",
    responsavelId: value[AUDIT_RESPONSAVEL_KEY] ?? [],
  };
}

/** E-mail de quem agiu, para a foto: `responsavelEmail` ou, se parecer e-mail, o próprio `responsavel`. */
function resolveResponsavelEmail(record: AuditRecord): string {
  if (isEmail(record.responsavelEmail)) return record.responsavelEmail.trim();
  if (isEmail(record.responsavel)) return record.responsavel.trim();

  return "";
}

export function buildAuditLinha(record: AuditRecord): AuditLinha {
  const cargoPerfil = [record.responsavelCargo, record.responsavelPerfil]
    .filter(Boolean)
    .join(" / ");
  const { date, time } = formatAuditDateTime(record.dataHora);
  const acao = record.acaoDescricao || record.acao;

  return {
    id: record.id,
    record,
    dataHora: { data: date, hora: time },
    acao,
    justificativa: resolveAuditJustificativa(record),
    modulo: getAuditModuleLabel(record),
    responsavel: record.responsavel
      ? {
          nome: record.responsavel,
          // O que aparece sob o nome na linha; o cartão mostra "cargo / perfil" quando o serviço os separa.
          detalhe: record.responsavelDetalhe,
          temCartao: record.responsavel.trim().toLowerCase() !== AUDIT_SYSTEM_USER,
          cartao: {
            email: resolveResponsavelEmail(record),
            telefone: record.responsavelTelefone,
            detalhe: cargoPerfil || record.responsavelDetalhe,
          },
          testIds: {
            trigger: `audit-responsavel-${record.id}`,
            card: `audit-responsavel-card-${record.id}`,
            copy: `audit-responsavel-copy-${record.id}`,
          },
        }
      : null,
    detailLabel: `Detalhar registro de ${acao || "auditoria"}`,
    detailTestId: `audit-detail-${record.id}`,
  };
}

/** Nomes legíveis das variáveis que os logs gravam; as demais são humanizadas a partir do camelCase. */
const AUDIT_VARIAVEIS: Readonly<Record<string, string>> = {
  status: "Status",
  versao: "Versão",
  qtdAlteracoes: "Qtd. de alterações",
  obra: "Obra",
  obraId: "ID da obra",
  obraCodigo: "Código da obra",
  embarcacao: "Embarcação",
  fichaId: "Ficha",
  nome: "Nome",
  descricao: "Descrição",
  codigo: "Código",
  motivo: "Motivo",
  recurso: "Recurso",
  formato: "Formato",
};

const AUDIT_SEM_VALOR = "—";

/** `qtdItens` → "Qtd itens"; `obra_id` → "Obra id". */
function humanizarVariavel(chave: string): string {
  const palavras = chave
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .toLowerCase();

  return palavras.charAt(0).toUpperCase() + palavras.slice(1);
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** Valor legível: Sim/Não, "—" no vazio e, em lista ou objeto, os itens em texto corrido (nunca JSON). */
function formatarValorDetalhe(value: unknown): string {
  if (value === null || value === undefined || value === "") return AUDIT_SEM_VALOR;
  if (typeof value === "boolean") return value ? "Sim" : "Não";
  if (Array.isArray(value)) {
    return value.length > 0 ? value.map(formatarValorDetalhe).join(", ") : AUDIT_SEM_VALOR;
  }
  if (isRecord(value)) {
    const itens = Object.entries(value).map(
      ([chave, item]) =>
        `${AUDIT_VARIAVEIS[chave] ?? humanizarVariavel(chave)}: ${formatarValorDetalhe(item)}`,
    );
    return itens.length > 0 ? itens.join("; ") : AUDIT_SEM_VALOR;
  }

  return String(value);
}

/** Objeto JSON do valor gravado; `undefined` quando vazio e `null` quando é outro texto. */
function parseObjetoJson(valor: string): Record<string, unknown> | undefined | null {
  if (!valor.trim()) return undefined;
  try {
    const parsed: unknown = JSON.parse(valor);

    return isRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Detalhe do evento para o modal quando o valor anterior e o novo são o JSON de variáveis do log: uma linha por
 * variável, `antes → depois` só no que mudou. Qualquer outro texto devolve `null` e o modal mostra os valores como vêm.
 */
export function formatAuditDetalhes(anterior: string, novo: string): AuditDetalheLinha[] | null {
  const antes = parseObjetoJson(anterior);
  const depois = parseObjetoJson(novo);
  if (antes === null || depois === null || (antes === undefined && depois === undefined)) {
    return null;
  }

  const chaves = [...new Set([...Object.keys(depois ?? {}), ...Object.keys(antes ?? {})])];

  return chaves.map((chave) => {
    const rotulo = `${AUDIT_VARIAVEIS[chave] ?? humanizarVariavel(chave)}:`;
    const valorAntes = formatarValorDetalhe(antes?.[chave]);
    const valorDepois = depois ? formatarValorDetalhe(depois[chave]) : AUDIT_SEM_VALOR;
    const mudou = antes !== undefined && (depois === undefined || valorAntes !== valorDepois);

    return mudou
      ? { chave, rotulo, anterior: valorAntes, valor: valorDepois }
      : { chave, rotulo, anterior: null, valor: valorDepois };
  });
}

/**
 * Justificativa em pares `Chave: valor` separados por `|` (ex.: exportação). "Sem filtros", que o core acrescenta à
 * exportação sem filtros, vira `Filtros: Sem filtros` e outra parte solta vira uma linha só com o valor. Sem nenhum par
 * `Chave: valor`, devolve `null` e o texto fica como veio.
 */
export function parseAuditPares(texto: string): AuditDetalheLinha[] | null {
  const partes = texto.split("|").map((parte) => parte.trim());
  if (partes.length < 2 || partes.some((parte) => parte === "")) return null;

  const pares = partes.map((parte) => /^([^:]+):\s*(.+)$/.exec(parte));
  if (pares.every((par) => par === null)) return null;

  return partes.map((parte, indice) => {
    const chave = `par-${indice}`;
    const par = pares[indice];
    if (par) return { chave, rotulo: `${par[1]!.trim()}:`, anterior: null, valor: par[2]! };
    if (/^sem filtros$/i.test(parte)) {
      return { chave, rotulo: "Filtros:", anterior: null, valor: parte };
    }

    return { chave, rotulo: "", anterior: null, valor: parte };
  });
}
